import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  Form,
  Button,
  Table,
  Accordion,
  Badge,
  Spinner,
  Modal,
} from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import PageLayout from "../../../layouts/PageLayout";

export default function EditActGroup() {
  const { id } = useParams();

  const AuthToken = localStorage.getItem("AuthUser");

  axios.defaults.headers.common[
    "Authorization"
  ] = `Bearer ${AuthToken}`;

  // ============================================================
  // MAIN STATES
  // ============================================================

  const [loading, setLoading] = useState(true);
  const [savingGroup, setSavingGroup] = useState(false);

  const [group, setGroup] = useState({
    user_id: "",
    city_id: "",
    address: "",
    group_name: "",
    group_gender: "",
    date: "",
    agreed_amount: "",
    saving_frequency: "",
    meeting_frequency: "",
    mobilizers: "",
    admission_from_status: "",
    saving_box_delivered: false,
    minute_book_delivered: false,
    loan_ledger_delivered: false,
    pass_book_delivered: false,
  });

  const [members, setMembers] = useState([]);
  const [cities, setCities] = useState([]);
  const [meetings, setMeetings] = useState([]);

  const [errors, setErrors] = useState({});

  // ============================================================
  // ADD MEMBER STATES
  // ============================================================

  const [showMemberModal, setShowMemberModal] =
    useState(false);

  const [savingMember, setSavingMember] =
    useState(false);

  const [memberErrors, setMemberErrors] =
    useState({});

  const [newMember, setNewMember] = useState({
    role: "member",
    name: "",
    cnic: "",
    mobile: "",
    status: true,
  });

  // ============================================================
  // ADD MEETING STATES
  // ============================================================

  const [showMeetingModal, setShowMeetingModal] =
    useState(false);

  const [savingMeeting, setSavingMeeting] =
    useState(false);

  const [meetingErrors, setMeetingErrors] =
    useState({});

  const [newMeeting, setNewMeeting] = useState({
    date: "",
    collected_amount: "",
    notes: "",
    images: [],
  });

  // ============================================================
  // LOAD GROUP
  // ============================================================

  useEffect(() => {
    fetchGroup();
  }, [id]);

  const fetchGroup = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/act-groups/${id}`
      );

      const data = response.data.data;
      setCities(response.data.cities || []);
      setGroup({
        user_id: data.user_id || "",
        city_id: data.city_id || "",
        address: data.address || "",
        group_name: data.group_name || "",
        group_gender: data.group_gender || "",
        date: data.date
          ? String(data.date).substring(0, 10)
          : "",
        agreed_amount: data.agreed_amount || "",
        saving_frequency:
          data.saving_frequency || "",
        meeting_frequency:
          data.meeting_frequency || "",
        mobilizers: Array.isArray(data.mobilizers)
          ? data.mobilizers
          : [],
        admission_from_status:
          data.admission_from_status || "",

        saving_box_delivered: Boolean(
          data.saving_box_delivered
        ),

        minute_book_delivered: Boolean(
          data.minute_book_delivered
        ),

        loan_ledger_delivered: Boolean(
          data.loan_ledger_delivered
        ),

        pass_book_delivered: Boolean(
          data.pass_book_delivered
        ),
      });

      setMembers(data.members || []);

      setMeetings(
        (data.meetings || []).map((meeting) => ({
          ...meeting,

          date: meeting.date
            ? String(meeting.date).substring(0, 10)
            : "",

          existing_images:
            meeting.images || [],

          new_images: [],
        }))
      );
    } catch (error) {
      console.error(
        "Error loading ACT Group:",
        error
      );

      Swal.fire({
        title: "Error!",
        text:
          error.response?.data?.message ||
          "Failed to load ACT Group.",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // GROUP FUNCTIONS
  // ============================================================

  const handleGroupChange = (e) => {
    const {
      name,
      value,
      type,
      checked,
    } = e.target;

    setGroup((prev) => ({
      ...prev,

      [name]:
        type === "checkbox"
          ? checked
          : value,
    }));
  };

  const updateGroup = async (e) => {
    e.preventDefault();

    try {
      setSavingGroup(true);
      setErrors({});

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/act-groups/${id}`,
        group
      );

      Swal.fire({
        title: "Success!",
        text:
          response.data.message ||
          "ACT Group updated successfully.",
        icon: "success",
      });
    } catch (error) {
      console.error(
        "Group update error:",
        error
      );

      if (error.response?.status === 422) {
        setErrors(
          error.response.data.errors || {}
        );
      }

      Swal.fire({
        title: "Error!",
        text:
          error.response?.data?.message ||
          "Failed to update ACT Group.",
        icon: "error",
      });
    } finally {
      setSavingGroup(false);
    }
  };

  // ============================================================
  // EXISTING MEMBER FUNCTIONS
  // ============================================================

  const handleMemberChange = (
    index,
    field,
    value
  ) => {
    setMembers((prev) => {
      const updated = [...prev];

      updated[index] = {
        ...updated[index],
        [field]: value,
      };

      return updated;
    });
  };

  const updateMember = async (
    member,
    index
  ) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/act-group-members/${member.id}`,
        {
          group_id: id,
          role: member.role,
          name: member.name,
          cnic: member.cnic,
          mobile: member.mobile || null,
          status: member.status ? 1 : 0,
        }
      );

      const updatedMember =
        response.data.data;

      setMembers((prev) => {
        const updated = [...prev];

        updated[index] = updatedMember;

        return updated;
      });

      Swal.fire({
        title: "Updated!",
        text: "Member updated successfully.",
        icon: "success",
      });
    } catch (error) {
      console.error(
        "Member update error:",
        error
      );

      let message =
        error.response?.data?.message ||
        "Failed to update member.";

      if (error.response?.status === 422) {
        const validationErrors =
          error.response.data.errors || {};

        message = Object.values(
          validationErrors
        )
          .flat()
          .join("\n");
      }

      Swal.fire({
        title: "Error!",
        text: message,
        icon: "error",
      });
    }
  };

  const deleteMember = async (
    member,
    index
  ) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Do you want to delete ${member.name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/act-group-members/${member.id}`
      );

      setMembers((prev) =>
        prev.filter(
          (_, memberIndex) =>
            memberIndex !== index
        )
      );

      Swal.fire({
        title: "Deleted!",
        text: "Member deleted successfully.",
        icon: "success",
      });
    } catch (error) {
      console.error(
        "Member delete error:",
        error
      );

      Swal.fire({
        title: "Error!",
        text:
          error.response?.data?.message ||
          "Failed to delete member.",
        icon: "error",
      });
    }
  };

  // ============================================================
  // ADD MEMBER FUNCTIONS
  // ============================================================

  const handleNewMemberChange = (e) => {
    const {
      name,
      value,
      type,
      checked,
    } = e.target;

    setNewMember((prev) => ({
      ...prev,

      [name]:
        type === "checkbox"
          ? checked
          : value,
    }));
  };

  const resetMemberForm = () => {
    setNewMember({
      role: "member",
      name: "",
      cnic: "",
      mobile: "",
      status: true,
    });

    setMemberErrors({});
  };

  const closeMemberModal = () => {
    setShowMemberModal(false);
    resetMemberForm();
  };

  const addMember = async (e) => {
    e.preventDefault();

    try {
      setSavingMember(true);
      setMemberErrors({});

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/act-group-members`,
        {
          group_id: id,
          role: newMember.role,
          name: newMember.name,
          cnic: newMember.cnic,
          mobile:
            newMember.mobile || null,
          status:
            newMember.status ? 1 : 0,
        }
      );

      setMembers((prev) => [
        ...prev,
        response.data.data,
      ]);

      closeMemberModal();

      Swal.fire({
        title: "Success!",
        text: "Member added successfully.",
        icon: "success",
      });
    } catch (error) {
      console.error(
        "Add member error:",
        error
      );

      if (error.response?.status === 422) {
        setMemberErrors(
          error.response.data.errors || {}
        );

        return;
      }

      Swal.fire({
        title: "Error!",
        text:
          error.response?.data?.message ||
          "Failed to add member.",
        icon: "error",
      });
    } finally {
      setSavingMember(false);
    }
  };

  // ============================================================
  // EXISTING MEETING FUNCTIONS
  // ============================================================

  const handleMeetingChange = (
    index,
    field,
    value
  ) => {
    setMeetings((prev) => {
      const updated = [...prev];

      updated[index] = {
        ...updated[index],
        [field]: value,
      };

      return updated;
    });
  };

  const handleMeetingImages = (
    index,
    files
  ) => {
    setMeetings((prev) => {
      const updated = [...prev];

      updated[index] = {
        ...updated[index],
        new_images: Array.from(
          files || []
        ),
      };

      return updated;
    });
  };

  const removeExistingImage = (
    meetingIndex,
    imageIndex
  ) => {
    setMeetings((prev) => {
      const updated = [...prev];

      const images = [
        ...(updated[meetingIndex]
          .existing_images || []),
      ];

      images.splice(
        imageIndex,
        1
      );

      updated[meetingIndex] = {
        ...updated[meetingIndex],
        existing_images: images,
      };

      return updated;
    });
  };

  const updateMeeting = async (
    meeting,
    index
  ) => {
    try {
      const formData = new FormData();

      formData.append(
        "group_id",
        id
      );

      formData.append(
        "date",
        meeting.date
      );

      formData.append(
        "collected_amount",
        meeting.collected_amount || 0
      );

      formData.append(
        "notes",
        meeting.notes || ""
      );

      (
        meeting.existing_images || []
      ).forEach((image) => {
        formData.append(
          "existing_images[]",
          image
        );
      });

      (
        meeting.new_images || []
      ).forEach((image) => {
        formData.append(
          "images[]",
          image
        );
      });

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/act-group-meetings/${meeting.id}`,
        formData,
        {
          headers: {
            "Content-Type":
              "multipart/form-data",
          },
        }
      );

      const updatedMeeting =
        response.data.data;

      setMeetings((prev) => {
        const updated = [...prev];

        updated[index] = {
          ...updatedMeeting,

          date: updatedMeeting.date
            ? String(
              updatedMeeting.date
            ).substring(0, 10)
            : "",

          existing_images:
            updatedMeeting.images || [],

          new_images: [],
        };

        return updated;
      });

      Swal.fire({
        title: "Updated!",
        text:
          "Meeting updated successfully.",
        icon: "success",
      });
    } catch (error) {
      console.error(
        "Meeting update error:",
        error
      );

      let message =
        error.response?.data?.message ||
        "Failed to update meeting.";

      if (error.response?.status === 422) {
        message = Object.values(
          error.response.data.errors || {}
        )
          .flat()
          .join("\n");
      }

      Swal.fire({
        title: "Error!",
        text: message,
        icon: "error",
      });
    }
  };

  const deleteMeeting = async (
    meeting,
    index
  ) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text:
        "This meeting will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/act-group-meetings/${meeting.id}`
      );

      setMeetings((prev) =>
        prev.filter(
          (_, meetingIndex) =>
            meetingIndex !== index
        )
      );

      Swal.fire({
        title: "Deleted!",
        text:
          "Meeting deleted successfully.",
        icon: "success",
      });
    } catch (error) {
      console.error(
        "Meeting delete error:",
        error
      );

      Swal.fire({
        title: "Error!",
        text:
          error.response?.data?.message ||
          "Failed to delete meeting.",
        icon: "error",
      });
    }
  };

  // ============================================================
  // ADD MEETING FUNCTIONS
  // ============================================================

  const handleNewMeetingChange = (e) => {
    const {
      name,
      value,
    } = e.target;

    setNewMeeting((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNewMeetingImages = (e) => {
    setNewMeeting((prev) => ({
      ...prev,

      images: Array.from(
        e.target.files || []
      ),
    }));
  };

  const resetMeetingForm = () => {
    setNewMeeting({
      date: "",
      collected_amount: "",
      notes: "",
      images: [],
    });

    setMeetingErrors({});
  };

  const closeMeetingModal = () => {
    setShowMeetingModal(false);
    resetMeetingForm();
  };

  const addMeeting = async (e) => {
    e.preventDefault();

    try {
      setSavingMeeting(true);
      setMeetingErrors({});

      const formData =
        new FormData();

      formData.append(
        "group_id",
        id
      );

      formData.append(
        "date",
        newMeeting.date
      );

      formData.append(
        "collected_amount",
        newMeeting.collected_amount ||
        0
      );

      formData.append(
        "notes",
        newMeeting.notes || ""
      );

      newMeeting.images.forEach(
        (image) => {
          formData.append(
            "images[]",
            image
          );
        }
      );

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/act-group-meetings`,
        formData,
        {
          headers: {
            "Content-Type":
              "multipart/form-data",
          },
        }
      );

      const created =
        response.data.data;

      setMeetings((prev) => [
        {
          ...created,

          date: created.date
            ? String(
              created.date
            ).substring(0, 10)
            : "",

          existing_images:
            created.images || [],

          new_images: [],
        },

        ...prev,
      ]);

      closeMeetingModal();

      Swal.fire({
        title: "Success!",
        text:
          "Meeting added successfully.",
        icon: "success",
      });
    } catch (error) {
      console.error(
        "Add meeting error:",
        error
      );

      if (error.response?.status === 422) {
        setMeetingErrors(
          error.response.data.errors || {}
        );

        return;
      }

      Swal.fire({
        title: "Error!",
        text:
          error.response?.data?.message ||
          "Failed to add meeting.",
        icon: "error",
      });
    } finally {
      setSavingMeeting(false);
    }
  };

  // ============================================================
  // IMAGE URL
  // ============================================================

  const getMeetingImageUrl = (
    image
  ) => {
    if (!image) {
      return "";
    }

    if (
      image.startsWith("http://") ||
      image.startsWith("https://")
    ) {
      return image;
    }

    const baseUrl =
      process.env.REACT_APP_IMG_URL ||
      "";

    return `${baseUrl}${image}`;
  };

  // ============================================================
  // LOADING
  // ============================================================

  if (loading) {
    return (
      <PageLayout
        style={{ height: "100%" }}
      >
        <div className="text-center py-5">

          <Spinner
            animation="border"
            variant="success"
          />

          <p className="mt-3">
            Loading ACT Group...
          </p>

        </div>
      </PageLayout>
    );
  }

  // ============================================================
  // JSX
  // ============================================================

  return (
    <PageLayout
      style={{ height: "100%" }}
    >

      {/* ======================================================
          BREADCRUMB
      ====================================================== */}

      <Row>
        <Col xl={12}>

          <div className="mc-card">

            <div className="mc-breadcrumb">

              <h3 className="mc-breadcrumb-title">

                <i className="material-icons">
                  groups
                </i>

                Edit ACT Group

              </h3>

              <ul className="mc-breadcrumb-list">

                <li className="mc-breadcrumb-item">

                  <Link
                    to="/"
                    className="mc-breadcrumb-link"
                    style={{
                      color: "#077834",
                    }}
                  >
                    Home
                  </Link>

                </li>

                <li className="mc-breadcrumb-item">

                  <Link
                    to="/list-act-groups"
                    className="mc-breadcrumb-link"
                    style={{
                      color: "#077834",
                    }}
                  >
                    ACT Groups
                  </Link>

                </li>

                <li className="mc-breadcrumb-item">
                  Edit
                </li>

              </ul>

            </div>

          </div>

        </Col>
      </Row>


      {/* ======================================================
          GROUP INFORMATION
      ====================================================== */}

      <Row>
        <Col xl={12}>

          <Card className="mb-4">

            <Card.Header>

              <h5 className="mb-0">
                Group Information
              </h5>

            </Card.Header>

            <Card.Body>

              <Form
                onSubmit={updateGroup}
              >

                <Row>

                  {/* Group Name */}

                  <Col
                    md={6}
                    className="mb-3"
                  >

                    <Form.Group>

                      <Form.Label>
                        Group Name
                      </Form.Label>

                      <Form.Control
                        type="text"
                        name="group_name"
                        value={
                          group.group_name
                        }
                        onChange={
                          handleGroupChange
                        }
                        isInvalid={
                          !!errors.group_name
                        }
                      />

                      <Form.Control.Feedback
                        type="invalid"
                      >
                        {
                          errors
                            .group_name?.[0]
                        }
                      </Form.Control.Feedback>

                    </Form.Group>

                  </Col>


                  {/* City */}

                  <Col
                    md={6}
                    className="mb-3"
                  >

                    <Form.Group>

                      <Form.Label>
                        City ID
                      </Form.Label>

                      <Form.Select
                        name="city_id"
                        value={group.city_id || ""}
                        onChange={handleGroupChange}
                        isInvalid={!!errors.city_id}
                      >
                        <option value="">Select City</option>

                        {cities?.map((city) => (
                          <option key={city.id} value={city.id}>
                            {city.name}
                          </option>
                        ))}
                      </Form.Select>

                      <Form.Control.Feedback type="invalid">
                        {errors.city_id}
                      </Form.Control.Feedback>

                      <Form.Control.Feedback
                        type="invalid"
                      >
                        {
                          errors
                            .city_id?.[0]
                        }
                      </Form.Control.Feedback>

                    </Form.Group>

                  </Col>


                  {/* Address */}

                  <Col
                    md={12}
                    className="mb-3"
                  >

                    <Form.Group>

                      <Form.Label>
                        Address
                      </Form.Label>

                      <Form.Control
                        type="text"
                        name="address"
                        value={
                          group.address
                        }
                        onChange={
                          handleGroupChange
                        }
                        isInvalid={
                          !!errors.address
                        }
                      />

                      <Form.Control.Feedback
                        type="invalid"
                      >
                        {
                          errors
                            .address?.[0]
                        }
                      </Form.Control.Feedback>

                    </Form.Group>

                  </Col>


                  {/* Gender */}

                  <Col
                    md={6}
                    className="mb-3"
                  >

                    <Form.Group>

                      <Form.Label>
                        Group Gender
                      </Form.Label>

                      <Form.Select
                        name="group_gender"
                        value={
                          group.group_gender
                        }
                        onChange={
                          handleGroupChange
                        }
                        isInvalid={
                          !!errors
                            .group_gender
                        }
                      >

                        <option value="">
                          Select Gender
                        </option>

                        <option value="1">
                          Male
                        </option>

                        <option value="2">
                          Female
                        </option>

                      </Form.Select>

                      <Form.Control.Feedback
                        type="invalid"
                      >
                        {
                          errors
                            .group_gender?.[0]
                        }
                      </Form.Control.Feedback>

                    </Form.Group>

                  </Col>


                  {/* Date */}

                  <Col
                    md={6}
                    className="mb-3"
                  >

                    <Form.Group>

                      <Form.Label>
                        Group Date
                      </Form.Label>

                      <Form.Control
                        type="date"
                        name="date"
                        value={group.date}
                        onChange={
                          handleGroupChange
                        }
                        isInvalid={
                          !!errors.date
                        }
                      />

                      <Form.Control.Feedback
                        type="invalid"
                      >
                        {
                          errors.date?.[0]
                        }
                      </Form.Control.Feedback>

                    </Form.Group>

                  </Col>


                  {/* Agreed Amount */}

                  <Col
                    md={6}
                    className="mb-3"
                  >

                    <Form.Group>

                      <Form.Label>
                        Agreed Amount
                      </Form.Label>

                      <Form.Control
                        type="number"
                        min="0"
                        step="0.01"
                        name="agreed_amount"
                        value={
                          group.agreed_amount
                        }
                        onChange={
                          handleGroupChange
                        }
                        isInvalid={
                          !!errors
                            .agreed_amount
                        }
                      />

                      <Form.Control.Feedback
                        type="invalid"
                      >
                        {
                          errors
                            .agreed_amount?.[0]
                        }
                      </Form.Control.Feedback>

                    </Form.Group>

                  </Col>


                  {/* Saving Frequency */}

                  <Col
                    md={6}
                    className="mb-3"
                  >

                    <Form.Group>

                      <Form.Label>
                        Saving Frequency
                      </Form.Label>

                      <Form.Control
                        type="text"
                        name="saving_frequency"
                        value={
                          group
                            .saving_frequency
                        }
                        onChange={
                          handleGroupChange
                        }
                        isInvalid={
                          !!errors
                            .saving_frequency
                        }
                      />

                      <Form.Control.Feedback
                        type="invalid"
                      >
                        {
                          errors
                            .saving_frequency?.[0]
                        }
                      </Form.Control.Feedback>

                    </Form.Group>

                  </Col>


                  {/* Meeting Frequency */}

                  <Col
                    md={6}
                    className="mb-3"
                  >

                    <Form.Group>

                      <Form.Label>
                        Meeting Frequency
                      </Form.Label>

                      <Form.Control
                        type="text"
                        name="meeting_frequency"
                        value={
                          group
                            .meeting_frequency
                        }
                        onChange={
                          handleGroupChange
                        }
                        isInvalid={
                          !!errors
                            .meeting_frequency
                        }
                      />

                      <Form.Control.Feedback
                        type="invalid"
                      >
                        {
                          errors
                            .meeting_frequency?.[0]
                        }
                      </Form.Control.Feedback>

                    </Form.Group>

                  </Col>


                  {/* Mobilizers */}

                  {/* Admission Status */}
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Mobilizers</Form.Label>

                      <Form.Select
                        multiple
                        name="mobilizers"
                        value={group.mobilizers || []}
                        onChange={(e) => {
                          const selectedValues = Array.from(
                            e.target.selectedOptions,
                            (option) => option.value
                          );

                          setGroup((prev) => ({
                            ...prev,
                            mobilizers: selectedValues,
                          }));
                        }}
                        isInvalid={!!errors.mobilizers}
                      >
                        {group.mobilizers.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </Form.Select>

                      {errors.mobilizers && (
                        <div className="text-danger mt-1">
                          {errors.mobilizers?.[0]}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                  <Col
                    md={6}
                    className="mb-3"
                  >

                    <Form.Group>

                      <Form.Label>
                        Admission Form Status
                      </Form.Label>

                      <Form.Control
                        type="text"
                        name="admission_from_status"
                        value={
                          group
                            .admission_from_status
                        }
                        onChange={
                          handleGroupChange
                        }
                        isInvalid={
                          !!errors
                            .admission_from_status
                        }
                      />

                      <Form.Control.Feedback
                        type="invalid"
                      >
                        {
                          errors
                            .admission_from_status?.[0]
                        }
                      </Form.Control.Feedback>

                    </Form.Group>

                  </Col>

                </Row>


                <hr />


                {/* Delivered Items */}

                <h6 className="mb-3">
                  Delivered Items
                </h6>

                <Row>

                  <Col
                    md={3}
                    className="mb-3"
                  >

                    <Form.Check
                      type="checkbox"
                      label="Saving Box"
                      name="saving_box_delivered"
                      checked={
                        group
                          .saving_box_delivered
                      }
                      onChange={
                        handleGroupChange
                      }
                    />

                  </Col>

                  <Col
                    md={3}
                    className="mb-3"
                  >

                    <Form.Check
                      type="checkbox"
                      label="Minute Book"
                      name="minute_book_delivered"
                      checked={
                        group
                          .minute_book_delivered
                      }
                      onChange={
                        handleGroupChange
                      }
                    />

                  </Col>

                  <Col
                    md={3}
                    className="mb-3"
                  >

                    <Form.Check
                      type="checkbox"
                      label="Loan Ledger"
                      name="loan_ledger_delivered"
                      checked={
                        group
                          .loan_ledger_delivered
                      }
                      onChange={
                        handleGroupChange
                      }
                    />

                  </Col>

                  <Col
                    md={3}
                    className="mb-3"
                  >

                    <Form.Check
                      type="checkbox"
                      label="Pass Book"
                      name="pass_book_delivered"
                      checked={
                        group
                          .pass_book_delivered
                      }
                      onChange={
                        handleGroupChange
                      }
                    />

                  </Col>

                </Row>


                <Button
                  type="submit"
                  variant="success"
                  disabled={
                    savingGroup
                  }
                >

                  {savingGroup
                    ? "Updating..."
                    : "Update Group"}

                </Button>

              </Form>

            </Card.Body>

          </Card>

        </Col>
      </Row>


      {/* ======================================================
          MEMBERS
      ====================================================== */}

      <Row>
        <Col xl={12}>

          <Card className="mb-4">

            <Card.Header className="d-flex justify-content-between align-items-center">

              <h5 className="mb-0">
                Group Members (
                {members.length})
              </h5>

              <Button
                variant="success"
                size="sm"
                onClick={() =>
                  setShowMemberModal(
                    true
                  )
                }
              >
                + Add Member
              </Button>

            </Card.Header>


            <Card.Body>

              <div className="table-responsive">

                <Table
                  bordered
                  hover
                  responsive
                >

                  <thead>

                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Role</th>
                      <th>CNIC</th>
                      <th>Mobile</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>

                  </thead>


                  <tbody>

                    {members.length >
                      0 ? (

                      members.map(
                        (
                          member,
                          index
                        ) => (

                          <tr
                            key={
                              member.id
                            }
                          >

                            <td>
                              {index + 1}
                            </td>


                            <td>

                              <Form.Control
                                type="text"
                                value={
                                  member.name ||
                                  ""
                                }
                                onChange={(
                                  e
                                ) =>
                                  handleMemberChange(
                                    index,
                                    "name",
                                    e
                                      .target
                                      .value
                                  )
                                }
                              />

                            </td>


                            <td>

                              <Form.Select
                                value={
                                  member.role ||
                                  "member"
                                }
                                onChange={(
                                  e
                                ) =>
                                  handleMemberChange(
                                    index,
                                    "role",
                                    e
                                      .target
                                      .value
                                  )
                                }
                              >

                                <option value="leader">
                                  Leader
                                </option>

                                <option value="secretary">
                                  Secretary
                                </option>

                                <option value="member">
                                  Member
                                </option>

                              </Form.Select>

                            </td>


                            <td>

                              <Form.Control
                                type="text"
                                value={
                                  member.cnic ||
                                  ""
                                }
                                onChange={(
                                  e
                                ) =>
                                  handleMemberChange(
                                    index,
                                    "cnic",
                                    e
                                      .target
                                      .value
                                  )
                                }
                              />

                            </td>


                            <td>

                              <Form.Control
                                type="text"
                                value={
                                  member.mobile ||
                                  ""
                                }
                                onChange={(
                                  e
                                ) =>
                                  handleMemberChange(
                                    index,
                                    "mobile",
                                    e
                                      .target
                                      .value
                                  )
                                }
                              />

                            </td>


                            <td>

                              <Form.Check
                                type="switch"
                                checked={
                                  Boolean(
                                    member.status
                                  )
                                }
                                onChange={(
                                  e
                                ) =>
                                  handleMemberChange(
                                    index,
                                    "status",
                                    e
                                      .target
                                      .checked
                                  )
                                }
                              />

                            </td>


                            <td>

                              <div className="d-flex gap-2">

                                <Button
                                  size="sm"
                                  variant="success"
                                  onClick={() =>
                                    updateMember(
                                      member,
                                      index
                                    )
                                  }
                                >
                                  Update
                                </Button>

                                <Button
                                  size="sm"
                                  variant="danger"
                                  onClick={() =>
                                    deleteMember(
                                      member,
                                      index
                                    )
                                  }
                                >
                                  Delete
                                </Button>

                              </div>

                            </td>

                          </tr>

                        )
                      )

                    ) : (

                      <tr>

                        <td
                          colSpan="7"
                          className="text-center py-4"
                        >
                          No members found.
                        </td>

                      </tr>

                    )}

                  </tbody>

                </Table>

              </div>

            </Card.Body>

          </Card>

        </Col>
      </Row>


      {/* ======================================================
          MEETINGS
      ====================================================== */}

      <Row>
        <Col xl={12}>

          <Card className="mb-4">

            <Card.Header className="d-flex justify-content-between align-items-center">

              <h5 className="mb-0">
                Group Meetings (
                {meetings.length})
              </h5>

              <Button
                variant="success"
                size="sm"
                onClick={() =>
                  setShowMeetingModal(
                    true
                  )
                }
              >
                + Add Meeting
              </Button>

            </Card.Header>


            <Card.Body>

              {meetings.length >
                0 ? (

                <Accordion>

                  {meetings.map(
                    (
                      meeting,
                      index
                    ) => (

                      <Accordion.Item
                        eventKey={String(
                          index
                        )}
                        key={
                          meeting.id
                        }
                      >

                        <Accordion.Header>

                          Meeting #
                          {index + 1}

                          {" - "}

                          {meeting.date}

                          {" - PKR "}

                          {Number(
                            meeting.collected_amount ||
                            0
                          ).toLocaleString(
                            "en-PK"
                          )}

                        </Accordion.Header>


                        <Accordion.Body>

                          <Row>

                            {/* Date */}

                            <Col
                              md={6}
                              className="mb-3"
                            >

                              <Form.Group>

                                <Form.Label>
                                  Meeting Date
                                </Form.Label>

                                <Form.Control
                                  type="date"
                                  value={
                                    meeting.date ||
                                    ""
                                  }
                                  onChange={(
                                    e
                                  ) =>
                                    handleMeetingChange(
                                      index,
                                      "date",
                                      e
                                        .target
                                        .value
                                    )
                                  }
                                />

                              </Form.Group>

                            </Col>


                            {/* Amount */}

                            <Col
                              md={6}
                              className="mb-3"
                            >

                              <Form.Group>

                                <Form.Label>
                                  Collected Amount
                                </Form.Label>

                                <Form.Control
                                  type="number"
                                  min="0"
                                  value={
                                    meeting.collected_amount ||
                                    ""
                                  }
                                  onChange={(
                                    e
                                  ) =>
                                    handleMeetingChange(
                                      index,
                                      "collected_amount",
                                      e
                                        .target
                                        .value
                                    )
                                  }
                                />

                              </Form.Group>

                            </Col>


                            {/* Notes */}

                            <Col
                              md={12}
                              className="mb-3"
                            >

                              <Form.Group>

                                <Form.Label>
                                  Notes
                                </Form.Label>

                                <Form.Control
                                  as="textarea"
                                  rows={4}
                                  value={
                                    meeting.notes ||
                                    ""
                                  }
                                  onChange={(
                                    e
                                  ) =>
                                    handleMeetingChange(
                                      index,
                                      "notes",
                                      e
                                        .target
                                        .value
                                    )
                                  }
                                />

                              </Form.Group>

                            </Col>


                            {/* Existing Images */}

                            <Col
                              md={12}
                              className="mb-3"
                            >

                              <Form.Label>
                                Existing Images
                              </Form.Label>

                              <div className="d-flex flex-wrap gap-3">

                                {meeting
                                  .existing_images
                                  ?.length >
                                  0 ? (

                                  meeting.existing_images.map(
                                    (
                                      image,
                                      imageIndex
                                    ) => (

                                      <div
                                        key={
                                          imageIndex
                                        }
                                        style={{
                                          position:
                                            "relative",
                                        }}
                                      >

                                        <img
                                          src={getMeetingImageUrl(
                                            image
                                          )}
                                          alt="Meeting"
                                          style={{
                                            width:
                                              "120px",
                                            height:
                                              "100px",
                                            objectFit:
                                              "cover",
                                            borderRadius:
                                              "6px",
                                            border:
                                              "1px solid #ddd",
                                          }}
                                        />

                                        <Button
                                          type="button"
                                          variant="danger"
                                          size="sm"
                                          style={{
                                            position:
                                              "absolute",
                                            top:
                                              "-8px",
                                            right:
                                              "-8px",
                                            borderRadius:
                                              "50%",
                                            width:
                                              "25px",
                                            height:
                                              "25px",
                                            padding:
                                              0,
                                          }}
                                          onClick={() =>
                                            removeExistingImage(
                                              index,
                                              imageIndex
                                            )
                                          }
                                        >
                                          ×
                                        </Button>

                                      </div>

                                    )
                                  )

                                ) : (

                                  <span className="text-muted">
                                    No existing images
                                  </span>

                                )}

                              </div>

                            </Col>


                            {/* New Images */}

                            <Col
                              md={12}
                              className="mb-3"
                            >

                              <Form.Group>

                                <Form.Label>
                                  Add New Images
                                </Form.Label>

                                <Form.Control
                                  type="file"
                                  multiple
                                  accept="image/*"
                                  onChange={(
                                    e
                                  ) =>
                                    handleMeetingImages(
                                      index,
                                      e
                                        .target
                                        .files
                                    )
                                  }
                                />

                              </Form.Group>

                              {meeting
                                .new_images
                                ?.length >
                                0 && (

                                  <div className="mt-2">

                                    <Badge bg="info">

                                      {
                                        meeting
                                          .new_images
                                          .length
                                      }{" "}
                                      new image(s)
                                      selected

                                    </Badge>

                                  </div>

                                )}

                            </Col>

                          </Row>


                          <div className="d-flex gap-2 mt-3">

                            <Button
                              variant="success"
                              onClick={() =>
                                updateMeeting(
                                  meeting,
                                  index
                                )
                              }
                            >
                              Update Meeting
                            </Button>

                            <Button
                              variant="danger"
                              onClick={() =>
                                deleteMeeting(
                                  meeting,
                                  index
                                )
                              }
                            >
                              Delete Meeting
                            </Button>

                          </div>

                        </Accordion.Body>

                      </Accordion.Item>

                    )
                  )}

                </Accordion>

              ) : (

                <div className="text-center text-muted py-4">

                  No meetings found.

                </div>

              )}

            </Card.Body>

          </Card>

        </Col>
      </Row>


      {/* ======================================================
          BOTTOM BUTTON
      ====================================================== */}

      <div className="mb-5">

        <Link to="/list-act-groups">

          <Button variant="secondary">
            Back to ACT Groups
          </Button>

        </Link>

      </div>


      {/* ======================================================
          ADD MEMBER MODAL
      ====================================================== */}

      <Modal
        show={showMemberModal}
        onHide={closeMemberModal}
        centered
        size="lg"
      >

        <Form onSubmit={addMember}>

          <Modal.Header closeButton>

            <Modal.Title>
              Add Group Member
            </Modal.Title>

          </Modal.Header>


          <Modal.Body>

            <Row>

              {/* Name */}

              <Col
                md={6}
                className="mb-3"
              >

                <Form.Group>

                  <Form.Label>
                    Member Name *
                  </Form.Label>

                  <Form.Control
                    type="text"
                    name="name"
                    value={
                      newMember.name
                    }
                    onChange={
                      handleNewMemberChange
                    }
                    placeholder="Enter member name"
                    isInvalid={
                      !!memberErrors.name
                    }
                  />

                  <Form.Control.Feedback
                    type="invalid"
                  >
                    {
                      memberErrors
                        .name?.[0]
                    }
                  </Form.Control.Feedback>

                </Form.Group>

              </Col>


              {/* Role */}

              <Col
                md={6}
                className="mb-3"
              >

                <Form.Group>

                  <Form.Label>
                    Role *
                  </Form.Label>

                  <Form.Select
                    name="role"
                    value={
                      newMember.role
                    }
                    onChange={
                      handleNewMemberChange
                    }
                    isInvalid={
                      !!memberErrors.role
                    }
                  >

                    <option value="leader">
                      Leader
                    </option>

                    <option value="secretary">
                      Secretary
                    </option>

                    <option value="member">
                      Member
                    </option>

                  </Form.Select>

                  <Form.Control.Feedback
                    type="invalid"
                  >
                    {
                      memberErrors
                        .role?.[0]
                    }
                  </Form.Control.Feedback>

                </Form.Group>

              </Col>


              {/* CNIC */}

              <Col
                md={6}
                className="mb-3"
              >

                <Form.Group>

                  <Form.Label>
                    CNIC *
                  </Form.Label>

                  <Form.Control
                    type="text"
                    name="cnic"
                    value={
                      newMember.cnic
                    }
                    onChange={
                      handleNewMemberChange
                    }
                    placeholder="00000-0000000-0"
                    isInvalid={
                      !!memberErrors.cnic
                    }
                  />

                  <Form.Control.Feedback
                    type="invalid"
                  >
                    {
                      memberErrors
                        .cnic?.[0]
                    }
                  </Form.Control.Feedback>

                </Form.Group>

              </Col>


              {/* Mobile */}

              <Col
                md={6}
                className="mb-3"
              >

                <Form.Group>

                  <Form.Label>
                    Mobile
                  </Form.Label>

                  <Form.Control
                    type="text"
                    name="mobile"
                    value={
                      newMember.mobile
                    }
                    onChange={
                      handleNewMemberChange
                    }
                    placeholder="03XXXXXXXXX"
                    isInvalid={
                      !!memberErrors.mobile
                    }
                  />

                  <Form.Control.Feedback
                    type="invalid"
                  >
                    {
                      memberErrors
                        .mobile?.[0]
                    }
                  </Form.Control.Feedback>

                </Form.Group>

              </Col>


              {/* Status */}

              <Col
                md={6}
                className="mb-3"
              >

                <Form.Check
                  type="switch"
                  name="status"
                  label="Active Member"
                  checked={
                    newMember.status
                  }
                  onChange={
                    handleNewMemberChange
                  }
                />

              </Col>

            </Row>

          </Modal.Body>


          <Modal.Footer>

            <Button
              type="button"
              variant="secondary"
              onClick={
                closeMemberModal
              }
            >
              Cancel
            </Button>

            <Button
              type="submit"
              variant="success"
              disabled={
                savingMember
              }
            >

              {savingMember
                ? "Saving..."
                : "Add Member"}

            </Button>

          </Modal.Footer>

        </Form>

      </Modal>


      {/* ======================================================
          ADD MEETING MODAL
      ====================================================== */}

      <Modal
        show={showMeetingModal}
        onHide={closeMeetingModal}
        centered
        size="lg"
      >

        <Form onSubmit={addMeeting}>

          <Modal.Header closeButton>

            <Modal.Title>
              Add Group Meeting
            </Modal.Title>

          </Modal.Header>


          <Modal.Body>

            <Row>

              {/* Date */}

              <Col
                md={6}
                className="mb-3"
              >

                <Form.Group>

                  <Form.Label>
                    Meeting Date *
                  </Form.Label>

                  <Form.Control
                    type="date"
                    name="date"
                    value={
                      newMeeting.date
                    }
                    onChange={
                      handleNewMeetingChange
                    }
                    isInvalid={
                      !!meetingErrors.date
                    }
                  />

                  <Form.Control.Feedback
                    type="invalid"
                  >
                    {
                      meetingErrors
                        .date?.[0]
                    }
                  </Form.Control.Feedback>

                </Form.Group>

              </Col>


              {/* Collected Amount */}

              <Col
                md={6}
                className="mb-3"
              >

                <Form.Group>

                  <Form.Label>
                    Collected Amount
                  </Form.Label>

                  <Form.Control
                    type="number"
                    min="0"
                    name="collected_amount"
                    value={
                      newMeeting
                        .collected_amount
                    }
                    onChange={
                      handleNewMeetingChange
                    }
                    placeholder="0"
                    isInvalid={
                      !!meetingErrors
                        .collected_amount
                    }
                  />

                  <Form.Control.Feedback
                    type="invalid"
                  >
                    {
                      meetingErrors
                        .collected_amount?.[0]
                    }
                  </Form.Control.Feedback>

                </Form.Group>

              </Col>


              {/* Notes */}

              <Col
                md={12}
                className="mb-3"
              >

                <Form.Group>

                  <Form.Label>
                    Meeting Notes
                  </Form.Label>

                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="notes"
                    value={
                      newMeeting.notes
                    }
                    onChange={
                      handleNewMeetingChange
                    }
                    placeholder="Enter meeting notes..."
                    isInvalid={
                      !!meetingErrors.notes
                    }
                  />

                  <Form.Control.Feedback
                    type="invalid"
                  >
                    {
                      meetingErrors
                        .notes?.[0]
                    }
                  </Form.Control.Feedback>

                </Form.Group>

              </Col>


              {/* Images */}

              <Col
                md={12}
                className="mb-3"
              >

                <Form.Group>

                  <Form.Label>
                    Meeting Images
                  </Form.Label>

                  <Form.Control
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={
                      handleNewMeetingImages
                    }
                    isInvalid={
                      !!meetingErrors.images
                    }
                  />

                  <Form.Control.Feedback
                    type="invalid"
                  >
                    {
                      meetingErrors
                        .images?.[0]
                    }
                  </Form.Control.Feedback>

                </Form.Group>


                {newMeeting.images
                  .length > 0 && (

                    <div className="mt-3">

                      <Badge bg="info">

                        {
                          newMeeting
                            .images
                            .length
                        }{" "}
                        image(s) selected

                      </Badge>

                    </div>

                  )}

              </Col>

            </Row>

          </Modal.Body>


          <Modal.Footer>

            <Button
              type="button"
              variant="secondary"
              onClick={
                closeMeetingModal
              }
            >
              Cancel
            </Button>

            <Button
              type="submit"
              variant="success"
              disabled={
                savingMeeting
              }
            >

              {savingMeeting
                ? "Saving..."
                : "Add Meeting"}

            </Button>

          </Modal.Footer>

        </Form>

      </Modal>

    </PageLayout>
  );
}